/**
 * Landing-state hero for /repositories when total === 0. Lazy-loads
 * CodeGraphScene so the three.js bundle (~150 KB) only downloads
 * for users with an empty install. Errors inside the scene surface
 * to Sentry via the ErrorBoundary wrapper.
 */
import { lazy, Suspense } from 'react';
import { isSentryEnabled, Sentry } from '../sentry.js';

const CodeGraphScene = lazy(() =>
  import('./CodeGraphScene.js').then((m) => ({ default: m.CodeGraphScene })),
);

interface EmptyRepositoriesHeroProps {
  title: string;
  body: string;
}

function SceneErrorFallback() {
  // When GL init fails, fall back to the plain card so the page
  // remains usable. `Sentry.ErrorBoundary` already captured the
  // underlying error to telemetry.
  return (
    <div className="feluda-empty-state">
      <h2 className="feluda-empty-state-title">No repositories yet</h2>
      <p className="feluda-empty-state-body">
        3D preview failed to load — the page still works.
      </p>
    </div>
  );
}

export function EmptyRepositoriesHero({ title, body }: EmptyRepositoriesHeroProps) {
  const scene = (
    <Suspense fallback={null}>
      <CodeGraphScene height={220} />
    </Suspense>
  );
  const bounded = isSentryEnabled() ? (
    <Sentry.ErrorBoundary fallback={<SceneErrorFallback />}>
      {scene}
    </Sentry.ErrorBoundary>
  ) : (
    scene
  );

  return (
    <div className="feluda-empty-hero" data-testid="empty-repositories-hero">
      {bounded}
      <h2 className="feluda-empty-hero-title">{title}</h2>
      <p className="feluda-empty-hero-body">{body}</p>
    </div>
  );
}
